import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import beginPaypalDeposit from '@api/payment/beginPaypalDeposit';
import completePaypalDeposit from '@api/payment/completePaypalDeposit';
import CoreButton from '@components/Buttons/CoreButton';
import useGetBalance from '@components/Hooks/useGetBalance';
import ModalContent from '@components/Modals/components/ModalContent';
import getPaymentStatusFromOrderState from '@components/Modals/helpers/getPaymentStatusFromOrderState';
import LoadingLogo from '@components/Modals/LoadingModal/Components/LoadingLogo';
import toRealMoney from '@helpers/toRealMoney';
import useAppState from '@hooks/API/useAppState';
import loadable from '@loadable/component';
import { captureMessage } from '@sentry/react';
import { useState, useRef } from 'react';
import { makeStyles } from 'tss-react/mui';
import { generateCorrelationId } from '@helpers/correlationId';
import { PayPalButtonWrapper } from '@components/Modals/AddFunds/components/PayPalButtonWrapper';
import { useFeature } from 'flagged';
import { useUserState } from '@context/UserContext';
const Coins = loadable(() => import('@components/Icons/Coins'));
const Dollar = loadable(() => import('@components/Icons/Dollar'));
const useStyles = makeStyles()((theme) => ({
    creditCard: {
        fill: '#FF5F00',
        height: '35px',
    },
    finePrint: {
        textAlign: 'center',
        fontSize: '12px',
        color: theme.palette.grey[500],
    },
    hidden: {
        display: 'none',
    },
    loading: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '200px',
    },
    paymentButtons: {
        '& button': {
            fontWeight: 600,
        },
    },
    transferButton: {
        width: '100%',
        marginBottom: '12px',
        display: 'flex',
        justifyContent: 'space-between',
        height: '35px',
        padding: 0,
        fontWeight: 600,
        color: theme.palette.grey[900],
        '&:hover': {
            color: theme.palette.grey[900],
        },
    },
    transferButtonIconLeft: {
        height: '35px',
        width: '35px',
        padding: theme.spacing(1),
        '& svg': {
            maxHeight: '100%',
            maxWidth: '100%',
            width: 'auto',
            height: '100%',
        },
        borderRight: `1px solid ${theme.palette.grey[200]}`,
    },
    transferButtonIconRight: {
        height: '35px',
        width: '35px',
        padding: theme.spacing(1),
        '& svg': {
            maxHeight: '100%',
            maxWidth: '100%',
            width: 'auto',
            height: '100%',
        },
        borderLeft: `1px solid ${theme.palette.grey[200]}`,
    },
}));
const PaymentMethod = ({ onFinishProcess, paypalOrderId, transactionAmount, currency, currencyAmount, setPaypalOrderId, setPaymentStatus, setTransactionWarningModalData, setError, paymentStatus, onBalanceTransfer, }) => {
    const { classes, cx } = useStyles();
    // Correlation ID for tracing PayPal deposit flow across frontend and backend
    const correlationIdRef = useRef(null);
    const [displayFinePrint, setDisplayFinePrint] = useState(false);
    const { refetch: refetchBalance } = useGetBalance();
    const { paymentMethods, isLoading, error } = useAppState();
    const { userDetails } = useUserState();
    const isUsePaymentMicroserviceEnabled = !!useFeature('use_payment_microservice');
    if (error || (!isLoading && !paymentMethods)) {
        return (_jsx(ModalContent, { align: "center", children: _jsxs("div", { className: classes.loading, children: [_jsx(LoadingLogo, {}), _jsx("p", { children: "An error has occurred, please refresh the page and try again" })] }) }));
    }
    if (isLoading || !paymentMethods) {
        return (_jsx(ModalContent, { align: "center", children: _jsxs("div", { className: classes.loading, children: [_jsx(LoadingLogo, {}), _jsx("p", { children: "Processing" })] }) }));
    }
    const { clientId, fixedFee, percentFee } = paymentMethods.paypal;
    const percentageFee = percentFee * 0.01;
    const minAmount = currency === 'PM' ? 5 : 10;
    const handleError = async () => {
        var _a;
        // prevent update order in cancel state to error
        if (paypalOrderId && paymentStatus !== 'WAITING') {
            try {
                await completePaypalDeposit({
                    paypalOrderId,
                    newOrderState: 'error',
                }, isUsePaymentMicroserviceEnabled, (_a = correlationIdRef.current) !== null && _a !== void 0 ? _a : '');
            }
            catch {
                // This is a best effort attempt to update the Order with the error state
            }
        }
        setPaymentStatus('ERROR');
        setPaypalOrderId(undefined);
        onFinishProcess();
    };
    const handleMinAmountNotMet = () => {
        setError({
            errorMessage: `The minimum amount for this transaction is $${toRealMoney(minAmount)} USD`,
            allowRetry: true,
        });
        return false;
    };
    const createOrder = async (data, actions) => {
        var _a, _b, _c, _d, _e;
        // Generate a new correlation ID at the start of each deposit flow
        const correlationId = generateCorrelationId();
        correlationIdRef.current = correlationId;
        if (!transactionAmount) {
            setPaymentStatus('ERROR');
            setError({ errorMessage: 'Please provide an amount for this transaction', allowRetry: true });
            return false;
        }
        const value = (transactionAmount + fixedFee) / (1.0 - percentageFee);
        if (value < minAmount) {
            return handleMinAmountNotMet();
        }
        const newPaypalOrderId = await actions.order.create({
            intent: 'CAPTURE',
            purchase_units: [
                {
                    amount: {
                        currency_code: 'USD',
                        value: ((transactionAmount + fixedFee) / (1.0 - percentageFee)).toFixed(2),
                    },
                },
            ],
            application_context: {
                shipping_preference: 'NO_SHIPPING', // default is "GET_FROM_FILE"
            },
        });
        if (!newPaypalOrderId) {
            throw new Error('Paypal create order did not return a new order ID');
        }
        setPaypalOrderId(newPaypalOrderId);
        try {
            const { orderState } = await beginPaypalDeposit({
                transactionType: currency === 'USD' ? 'deposit' : 'purchase',
                amount: transactionAmount,
                coins: currency === 'PM' ? currencyAmount : null,
                paypalOrderId: newPaypalOrderId,
                country: userDetails === null || userDetails === void 0 ? void 0 : userDetails.country,
                state: userDetails === null || userDetails === void 0 ? void 0 : userDetails.state,
            }, isUsePaymentMicroserviceEnabled, correlationId);
            setPaymentStatus(getPaymentStatusFromOrderState(orderState));
        }
        catch (err) {
            // ErrorWithCause wraps the original axios error in the 'cause' property
            // but also exposes 'response' directly (see ErrorWithCause.ts line 20)
            const axiosErr = err;
            const dataResponse = ((_a = axiosErr.response) === null || _a === void 0 ? void 0 : _a.data) || ((_c = (_b = axiosErr.cause) === null || _b === void 0 ? void 0 : _b.response) === null || _c === void 0 ? void 0 : _c.data);
            // set state to show deposit warning modal when user violate deposit rule
            if (dataResponse && dataResponse.name === 'DEPOSIT_LIMIT_REACHED') {
                setTransactionWarningModalData({
                    isShow: true,
                    nextLifted: (_d = dataResponse.nextLifted) !== null && _d !== void 0 ? _d : null,
                    errorCode: (_e = dataResponse.name) !== null && _e !== void 0 ? _e : null,
                });
                throw err;
            }
        }
        return newPaypalOrderId;
    };
    const handleCancel = async (data) => {
        var _a;
        // prevent update order being in progress or in error state to cancel
        if ((data === null || data === void 0 ? void 0 : data.orderID) && paymentStatus !== 'ERROR' && paymentStatus !== 'VALIDATING') {
            // This is a best effort attempt to update the Order with the cancelled state - kick it off and ignore any exceptions
            try {
                await completePaypalDeposit({
                    paypalOrderId: data.orderID,
                    newOrderState: 'cancelled',
                }, isUsePaymentMicroserviceEnabled, (_a = correlationIdRef.current) !== null && _a !== void 0 ? _a : '');
            }
            catch { }
        }
        setPaymentStatus('WAITING');
    };
    const handleSuccess = async (details, data) => {
        var _a;
        setPaymentStatus('VALIDATING');
        // Extract orderID from the approval data (second parameter)
        const orderId = (data === null || data === void 0 ? void 0 : data.orderID) || (details === null || details === void 0 ? void 0 : details.id);
        if (!orderId) {
            captureMessage('Successful paypal order but no order ID', 'fatal');
            setError({
                errorMessage: 'Something went wrong after your payment was taken. Please contact support immediately.',
                allowRetry: false,
            });
            void handleError();
            return;
        }
        let completePaypalDepositResponse;
        try {
            completePaypalDepositResponse = await completePaypalDeposit({
                paypalOrderId: orderId,
                newOrderState: 'completed',
                transaction: details,
            }, isUsePaymentMicroserviceEnabled, (_a = correlationIdRef.current) !== null && _a !== void 0 ? _a : '');
        }
        catch {
            setError({
                errorMessage: 'Something went wrong after your payment was taken. Please contact support immediately.',
                allowRetry: false,
            });
            void handleError();
            return;
        }
        setPaymentStatus(getPaymentStatusFromOrderState(completePaypalDepositResponse.orderState));
        void refetchBalance();
        onFinishProcess();
    };
    const onPaypalButtonReady = () => {
        setDisplayFinePrint(true);
    };
    return (_jsxs(ModalContent, { align: "center", children: [paymentStatus === 'VALIDATING' && (_jsxs("div", { className: classes.loading, children: [_jsx(LoadingLogo, {}), _jsx("p", { children: "Processing Payment" })] })), _jsxs("div", { className: cx(paymentStatus === 'VALIDATING' && classes.hidden, classes.paymentButtons), children: [currency === 'PM' && (_jsxs(CoreButton, { variant: "grey", appearance: "outline", size: "sm", className: classes.transferButton, onClick: onBalanceTransfer, children: [_jsx("div", { className: classes.transferButtonIconLeft, children: _jsx(Dollar, {}) }), "Convert Cash to Coins", _jsx("div", { className: classes.transferButtonIconRight, children: _jsx(Coins, {}) })] })), _jsx(PayPalButtonWrapper, { options: {
                            clientId,
                        }, onError: handleError, createOrder: createOrder, onSuccess: handleSuccess, onCancel: handleCancel, onButtonReady: onPaypalButtonReady })] }), displayFinePrint && (_jsxs("p", { className: classes.finePrint, children: ["Deposit Fee: ", fixedFee.toLocaleString(undefined, { style: 'currency', currency: 'USD' }), " +", ' ', percentageFee.toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 1 }), " of the transaction amount"] }))] }));
};
export default PaymentMethod;
